import { useMutation } from "@apollo/client/react";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { backendUrl } from "@repo/graphql/cache/url";
import ReactNativeFile from "@repo/graphql/client/file";
import { UploadMediaDocument } from "@repo/graphql/documents/campaignDraft/mutation.generated";
import { DraftUploadMediaType } from "@repo/graphql/gql/graphql";
import axios from "axios";
import { fetch } from "expo/fetch";
import { TrashIcon } from "lucide-react-native";
import React, { useEffect, useState } from "react";
import { Button, ButtonIcon } from "../../ui/button";
import { Card } from "../../ui/card";
import { Heading } from "../../ui/heading";
import { HStack } from "../../ui/hstack";
import { Progress, ProgressFilledTrack } from "../../ui/progress";
import { VStack } from "../../ui/vstack";
import formatBytesToHuman from "../../utils/bytes";

type Props = {
  file: ReactNativeFile;
  onChange: (value: ReactNativeFile[]) => void;
  value: ReactNativeFile[];
};

const DraftImageCard = React.memo((props: Props) => {
  const { file, onChange, value } = props;
  const [progress, setProgress] = useState(file.externalId ? 100 : 0);
  const [uploading, setUploading] = useState(false);
  const [uploadDraftImage, { loading }] = useMutation(UploadMediaDocument);
  useEffect(() => {
    if (!file.externalId && !uploading && !loading && progress < 100) {
      uploadDraftImage({
        variables: {
          draftMedia: {
            fileName: file.name,
            mediaType: DraftUploadMediaType.Image,
            width: 0,
            height: 0,
            duration: 0.0,
            mimeType: file.type,
            fileSize: file.fileSize ?? 0,
          },
        },
        onCompleted: async (uploadDraftImage) => {
          setUploading(true);
          const draftMedia = uploadDraftImage.campaignDraft.uploadMedia.draftMedia;
          const uploadUrl = new URL(
            uploadDraftImage.campaignDraft.uploadMedia.uploadUrl,
            backendUrl,
          ).toString();
          const fetchResponse = await fetch(file.uri);
          const blob = await fetchResponse.blob();
          await axios.put(uploadUrl, blob, {
            onUploadProgress: (progressEvent) => {
              setProgress(Math.round((progressEvent.progress ?? 0) * 100));
            },
            baseURL: backendUrl,
            headers: {
              "Content-Type": file.type,
            },
          });
          setUploading(false);
          file.setExternalId(draftMedia.id);
          onChange(
            value.map((v) => (v.externalId === file.externalId ? file : v)),
          );
        },
      });
    }
  }, [file, uploadDraftImage, loading, uploading, onChange, progress, value]);
  return (
    <Card
      variant="filled"
      size="md"
      className={uploading || loading ? "opacity-50" : ""}
    >
      <HStack space="md" className="items-start">
        <Image
          className="rounded-xl"
          source={{ uri: file.uri }}
          size="xs"
          alt="Foto de la campaña"
        />
        <VStack space="xs" className="flex-1 shrink">
          <Heading size="2xs">{file.name}</Heading>
          <Text action="muted" size="xs">
            {formatBytesToHuman(file.fileSize ?? 0)}
          </Text>
          <Progress
            value={progress}
            size="xs"
            className="mt-3"
            action="brand"
            max={100}
          >
            <ProgressFilledTrack />
          </Progress>
        </VStack>
        <Button
          size="none"
          variant="link"
          onPress={() => {
            onChange(
              value.filter((rf: ReactNativeFile) => rf._id !== file._id),
            );
          }}
        >
          <ButtonIcon as={TrashIcon} size="xs" />
        </Button>
      </HStack>
    </Card>
  );
});

DraftImageCard.displayName = "DraftImageCard";

export default DraftImageCard;
